//src/pages/gro/SurveyCampaignFormPage.tsx
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CircleHelp, Loader2, Save } from "lucide-react";
import { toast } from "sonner";

import { listQuestionnaireVersions } from "@/services/questionnaires";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SurveyAudiencePreview } from "@/components/gro/SurveyAudiencePreview";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { useQuestionnaires } from "@/hooks/useQuestionnaires";
import {
  useCreateSurveyCampaign,
  usePreviewSurveyCampaignAudience,
  useSurveyCampaignDetail,
  useUpdateSurveyCampaign,
} from "@/hooks/useSurveyCampaigns";
import { useUnits } from "@/hooks/useUnits";
import { useDepartments } from "@/hooks/useDepartments";
import { useJobPositions } from "@/hooks/useJobPositions";
import { useWorkerGroups } from "@/hooks/useWorkerGroups";
import { useRiskInventories } from "@/hooks/useRiskInventories";
import type {
  QuestionnaireVersion,
  SurveyCampaignCreateIn,
  SurveyCampaignUpdateIn,
} from "@/types/gro";

const emptyForm: SurveyCampaignCreateIn = {
  questionnaire_version_id: "",
  name: "",
  description: "",
  starts_at: null,
  ends_at: null,
  anonymous_mode: "identified",
  anonymous_response_tolerance_percent: 5,
  default_inventory_id: null,
  filters: {
    unit_ids: [],
    department_ids: [],
    job_position_ids: [],
    worker_group_ids: [],
  },
};

const FILTERS_HELP_TEXT =
  "Os filtros possuem dependências. Exemplo: selecionar o cargo Analista e uma Unidade significa buscar usuários com esse cargo nessa unidade. Se não houver ninguém nessa combinação, a campanha não será enviada para ninguém. Grupo de Avaliação é independente dos filtros de unidade, departamento e cargo, podendo ser usado sozinho ou em conjunto. Se um usuário estiver em mais de um filtro, ele deve receber apenas uma pesquisa.";

function toggleId(list: string[], id: string) {
  return list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
}

function toDatetimeLocal(value?: string | null) {
  if (!value) return "";

  const match = String(value).match(
    /^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})/,
  );

  if (match) {
    const [, year, month, day, hour, minute] = match;
    return `${year}-${month}-${day}T${hour}:${minute}`;
  }

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}

function versionLabel(version: QuestionnaireVersion) {
  return version.version_label || `v${version.version_number}`;
}

function getQuestionnaireBaseName(
  version: QuestionnaireVersion,
  questionnairesMap: Record<string, string>,
) {
  const directName =
    (version as any).questionnaire_name ||
    (version as any).questionnaire_title ||
    (version as any).questionnaire?.name ||
    (version as any).questionnaire?.title ||
    (version as any).name ||
    (version as any).title;

  if (directName && String(directName).trim()) {
    return String(directName).trim();
  }

  const mappedName = questionnairesMap[(version as any).questionnaire_id];
  if (mappedName && mappedName.trim()) {
    return mappedName.trim();
  }

  return "Questionário";
}

function questionnaireOptionLabel(
  version: QuestionnaireVersion,
  questionnairesMap: Record<string, string>,
) {
  return `${getQuestionnaireBaseName(version, questionnairesMap)} - ${versionLabel(version)}`;
}

function hasAnyAudienceFilter(filters: SurveyCampaignCreateIn["filters"]) {
  return (
    (filters.unit_ids?.length || 0) > 0 ||
    (filters.department_ids?.length || 0) > 0 ||
    (filters.job_position_ids?.length || 0) > 0 ||
    (filters.worker_group_ids?.length || 0) > 0
  );
}

type LookupItem = {
  id: string;
  name: string;
};

type FilterListProps = {
  label: string;
  items: LookupItem[];
  selectedIds: string[];
  emptyMessage: string;
  disabled?: boolean;
  onToggle: (id: string) => void;
};

function FilterList({
  label,
  items,
  selectedIds,
  emptyMessage,
  disabled = false,
  onToggle,
}: FilterListProps) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="mt-2 max-h-[260px] overflow-auto rounded-lg border p-3 space-y-2">
        {items.length === 0 ? (
          <div className="rounded-md border border-dashed bg-muted/30 px-3 py-4 text-sm text-muted-foreground">
            {emptyMessage}
          </div>
        ) : (
          items.map((item) => (
            <label
              key={item.id}
              className={`flex items-start gap-3 py-1.5 ${
                disabled ? "cursor-not-allowed opacity-70" : "cursor-pointer"
              }`}
            >
              <Checkbox
                checked={selectedIds.includes(item.id)}
                disabled={disabled}
                onCheckedChange={() => onToggle(item.id)}
              />
              <div className="text-sm">{item.name}</div>
            </label>
          ))
        )}
      </div>
    </div>
  );
}

export default function SurveyCampaignFormPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();

  const isEditing = location.pathname.endsWith("/editar");
  const detailQ = useSurveyCampaignDetail(id);

  const createMut = useCreateSurveyCampaign();
  const updateMut = useUpdateSurveyCampaign();
  const previewMut = usePreviewSurveyCampaignAudience();

  const questionnairesQ = useQuestionnaires({
    include_inactive: false,
    q: "",
  });
  const unitsQ = useUnits();
  const departmentsQ = useDepartments();
  const jobPositionsQ = useJobPositions();
  const workerGroupsQ = useWorkerGroups({
    include_inactive: false,
    q: "",
    group_type: "",
  });
  const inventoriesQ = useRiskInventories({ status: undefined, unit_id: undefined });

  const questionnaireItems = questionnairesQ.data ?? [];
  const units = unitsQ.data ?? [];
  const departments = departmentsQ.data ?? [];
  const jobPositions = jobPositionsQ.data ?? [];
  const workerGroups = workerGroupsQ.data ?? [];
  const inventories = inventoriesQ.data ?? [];

  const questionnairesMap = useMemo(
    () =>
      Object.fromEntries(
        questionnaireItems.map((item: any) => [
          item.id,
          item.name || item.title || "Questionário",
        ]),
      ) as Record<string, string>,
    [questionnaireItems],
  );

  const unitsMap = useMemo(
    () =>
      Object.fromEntries(units.map((item: any) => [item.id, item.name])) as Record<
        string,
        string
      >,
    [units],
  );

  const departmentsMap = useMemo(
    () =>
      Object.fromEntries(
        departments.map((item: any) => [item.id, item.name]),
      ) as Record<string, string>,
    [departments],
  );

  const jobPositionsMap = useMemo(
    () =>
      Object.fromEntries(
        jobPositions.map((item: any) => [item.id, item.name]),
      ) as Record<string, string>,
    [jobPositions],
  );

  const [form, setForm] = useState<SurveyCampaignCreateIn>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [previewItems, setPreviewItems] = useState<any[]>([]);
  const [questionnaireVersions, setQuestionnaireVersions] = useState<QuestionnaireVersion[]>([]);

  useEffect(() => {
    if (!detailQ.data) return;

    setForm({
      questionnaire_version_id: detailQ.data.questionnaire_version_id,
      name: detailQ.data.name,
      description: detailQ.data.description || "",
      starts_at: detailQ.data.starts_at,
      ends_at: detailQ.data.ends_at,
      anonymous_mode: detailQ.data.anonymous_mode,
      anonymous_response_tolerance_percent: detailQ.data.anonymous_response_tolerance_percent ?? 5,
      default_inventory_id: (detailQ.data as any).default_inventory_id ?? null,
      filters: detailQ.data.filters,
    });

    setPreviewItems(detailQ.data.participants_preview || []);
  }, [detailQ.data]);

  useEffect(() => {
    let mounted = true;

    async function loadPublishedVersions() {
      if (!questionnaireItems.length) {
        setQuestionnaireVersions([]);
        return;
      }

      const versionGroups = await Promise.all(
        questionnaireItems.map(async (questionnaire: any) => {
          try {
            const versions = await listQuestionnaireVersions(questionnaire.id);
            return versions
              .filter((v) => v.status === "published")
              .map((v) => ({
                ...v,
                questionnaire_name:
                  (v as any).questionnaire_name ||
                  questionnaire.name ||
                  questionnaire.title ||
                  "Questionário",
              }));
          } catch {
            return [];
          }
        }),
      );

      if (mounted) {
        setQuestionnaireVersions(versionGroups.flat());
      }
    }

    void loadPublishedVersions();

    return () => {
      mounted = false;
    };
  }, [questionnaireItems]);

  const isLocked = useMemo(
    () => isEditing && detailQ.data && detailQ.data.status !== "draft",
    [detailQ.data, isEditing],
  );

  const filters = form.filters;

  function goBack() {
    navigate("/gro/campaigns");
  }

  function getMissingPreviewFields() {
    const missing: string[] = [];

    if (!form.name?.trim()) missing.push("nome da campanha");
    if (!form.questionnaire_version_id) missing.push("questionário");
    if (!form.starts_at) missing.push("data de início");
    if (!form.ends_at) missing.push("data de fim");

    return missing;
  }

  async function handlePreview() {
    const missing = getMissingPreviewFields();

    if (missing.length > 0) {
      toast.warning(
        `Preencha ${missing.join(", ")} para gerar o preview do público.`,
      );
      return;
    }

    if (!hasAnyAudienceFilter(form.filters)) {
      toast.warning(
        "Selecione ao menos um filtro de público-alvo: unidade, departamento, cargo ou grupo de avaliação.",
      );
      setPreviewItems([]);
      return;
    }

    try {
      const items = await previewMut.mutateAsync({
        ...form,
        starts_at: form.starts_at || null,
        ends_at: form.ends_at || null,
      });
      setPreviewItems(items);
      toast.success("Preview do público gerado com sucesso.");
    } catch (e: any) {
      toast.error(e?.message || "Erro ao gerar preview do público.");
    }
  }

  async function handleSave() {
    if (!form.name.trim()) {
      toast.error("Informe o nome da campanha.");
      return;
    }

    if (!form.questionnaire_version_id) {
      toast.error("Selecione um questionário.");
      return;
    }

    if (!form.default_inventory_id) {
      toast.error("Selecione um inventário padrão.");
      return;
    }

    if (isLocked) {
      toast.error("Somente campanhas em rascunho podem ser editadas.");
      return;
    }

    setSaving(true);
    try {
      if (isEditing && id) {
        await updateMut.mutateAsync({
          id,
          body: form as SurveyCampaignUpdateIn,
        });
        toast.success("Campanha atualizada com sucesso.");
      } else {
        await createMut.mutateAsync(form);
        toast.success("Campanha criada com sucesso.");
      }

      navigate("/gro/campaigns");
    } catch (e: any) {
      toast.error(e?.message || "Erro ao salvar campanha.");
    } finally {
      setSaving(false);
    }
  }

  const loading =
    questionnairesQ.isLoading ||
    unitsQ.isLoading ||
    departmentsQ.isLoading ||
    jobPositionsQ.isLoading ||
    workerGroupsQ.isLoading ||
    inventoriesQ.isLoading ||
    (isEditing && detailQ.isLoading);

  const hasLoadError =
    questionnairesQ.isError ||
    unitsQ.isError ||
    departmentsQ.isError ||
    jobPositionsQ.isError ||
    workerGroupsQ.isError ||
    inventoriesQ.isError ||
    (isEditing && detailQ.isError);

  const pageTitle = isEditing ? "Editar Campanha" : "Nova Campanha";
  const pageDescription = isEditing
    ? "Atualize os dados da campanha e revise o público-alvo."
    : "Crie uma campanha e defina o público-alvo da pesquisa.";

  return (
    <div className="pb-10">
      <div className="sticky top-14 z-20 -mx-1 mb-6 border-b bg-background/95 px-1 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex flex-col gap-3 py-4 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={goBack}>
              <ArrowLeft className="h-5 w-5" />
            </Button>

            <div>
              <h1 className="text-xl font-bold tracking-tight">{pageTitle}</h1>
              <p className="text-sm text-muted-foreground">{pageDescription}</p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 self-end md:self-auto">
            <Button variant="outline" onClick={goBack} disabled={saving}>
              Voltar
            </Button>

            <Button onClick={handleSave} disabled={saving || loading || !!isLocked}>
              {saving ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              Salvar
            </Button>
          </div>
        </div>
      </div>

      {loading ? (
        <Card className="p-6 text-sm text-muted-foreground">
          Carregando formulário...
        </Card>
      ) : hasLoadError ? (
        <Card className="p-6">
          <div className="text-sm text-destructive">
            Erro ao carregar os dados do formulário.
          </div>
        </Card>
      ) : (
        <div className="space-y-6">
          <Card>
            <CardContent className="grid grid-cols-1 gap-4 pt-6 md:grid-cols-2">
              <div className="md:col-span-2">
                <Label>Nome *</Label>
                <Input
                  className="mt-1"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Ex.: Pesquisa Psicossocial 2026.1"
                  disabled={!!isLocked}
                />
              </div>

              <div className="md:col-span-2">
                <Label>Descrição</Label>
                <Input
                  className="mt-1"
                  value={form.description ?? ""}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  placeholder="Descrição opcional"
                  disabled={!!isLocked}
                />
              </div>

              <div>
                <Label>Questionário *</Label>
                <Select
                  value={form.questionnaire_version_id}
                  onValueChange={(value) =>
                    setForm((prev) => ({
                      ...prev,
                      questionnaire_version_id: value,
                    }))
                  }
                  disabled={!!isLocked}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {questionnaireVersions.map((version) => (
                      <SelectItem key={version.id} value={version.id}>
                        {questionnaireOptionLabel(version, questionnairesMap)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Modo</Label>
                <Select
                  value={form.anonymous_mode}
                  onValueChange={(value: "identified" | "anonymous" | "pseudonymized") =>
                    setForm((prev) => ({
                      ...prev,
                      anonymous_mode: value,
                    }))
                  }
                  disabled={!!isLocked}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="identified">Identificada</SelectItem>
                    <SelectItem value="anonymous">Anônima</SelectItem>
                    <SelectItem value="pseudonymized">Pseudonimizada</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {form.anonymous_mode === "anonymous" ? (
                <div>
                  <Label>Tolerância de respostas (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    className="mt-1"
                    value={form.anonymous_response_tolerance_percent ?? 5}
                    disabled={!!isLocked}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        anonymous_response_tolerance_percent: e.target.value === "" ? null : Number(e.target.value),
                      }))
                    }
                  />
                  <p className="mt-1 text-xs text-muted-foreground">
                    Percentual adicional de respostas permitido apenas para campanhas anônimas.
                  </p>
                </div>
              ) : null}

              <div>
                <Label>Inventário padrão</Label>
                <Select
                  value={form.default_inventory_id || "__none__"}
                  onValueChange={(value) =>
                    setForm((prev) => ({
                      ...prev,
                      default_inventory_id: value === "__none__" ? null : value,
                    }))
                  }
                  disabled={!!isLocked}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Nenhum inventário padrão" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Nenhum inventário padrão</SelectItem>
                    {inventories.map((inventory: any) => (
                      <SelectItem key={inventory.id} value={inventory.id}>
                        {inventory.name || inventory.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Início</Label>
                <Input
                  type="datetime-local"
                  className="mt-1"
                  value={toDatetimeLocal(form.starts_at)}
                  disabled={!!isLocked}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      starts_at: e.target.value || null,
                    }))
                  }
                />
              </div>

              <div>
                <Label>Fim</Label>
                <Input
                  type="datetime-local"
                  className="mt-1"
                  value={toDatetimeLocal(form.ends_at)}
                  disabled={!!isLocked}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      ends_at: e.target.value || null,
                    }))
                  }
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="mb-4 flex items-center gap-2">
                <Label className="text-base">Filtros do público-alvo</Label>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="inline-flex h-5 w-5 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground"
                      aria-label="Ajuda sobre os filtros do público-alvo"
                    >
                      <CircleHelp className="h-4 w-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-[360px] text-sm leading-5">
                    {FILTERS_HELP_TEXT}
                  </TooltipContent>
                </Tooltip>
              </div>

              <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
                <FilterList
                  label="Unidades"
                  items={units}
                  selectedIds={filters.unit_ids}
                  emptyMessage="Nenhuma unidade encontrada."
                  disabled={!!isLocked}
                  onToggle={(itemId) =>
                    setForm((prev) => ({
                      ...prev,
                      filters: {
                        ...prev.filters,
                        unit_ids: toggleId(prev.filters.unit_ids, itemId),
                      },
                    }))
                  }
                />

                <FilterList
                  label="Departamentos"
                  items={departments}
                  selectedIds={filters.department_ids}
                  emptyMessage="Nenhum departamento encontrado."
                  disabled={!!isLocked}
                  onToggle={(itemId) =>
                    setForm((prev) => ({
                      ...prev,
                      filters: {
                        ...prev.filters,
                        department_ids: toggleId(prev.filters.department_ids, itemId),
                      },
                    }))
                  }
                />

                <FilterList
                  label="Cargos"
                  items={jobPositions}
                  selectedIds={filters.job_position_ids}
                  emptyMessage="Nenhum cargo encontrado."
                  disabled={!!isLocked}
                  onToggle={(itemId) =>
                    setForm((prev) => ({
                      ...prev,
                      filters: {
                        ...prev.filters,
                        job_position_ids: toggleId(prev.filters.job_position_ids, itemId),
                      },
                    }))
                  }
                />

                <FilterList
                  label="Grupos de avaliação"
                  items={workerGroups}
                  selectedIds={filters.worker_group_ids}
                  emptyMessage="Nenhum grupo de avaliação encontrado."
                  disabled={!!isLocked}
                  onToggle={(itemId) =>
                    setForm((prev) => ({
                      ...prev,
                      filters: {
                        ...prev.filters,
                        worker_group_ids: toggleId(prev.filters.worker_group_ids, itemId),
                      },
                    }))
                  }
                />
              </div>
            </CardContent>
          </Card>

          <SurveyAudiencePreview
            items={previewItems}
            unitsMap={unitsMap}
            departmentsMap={departmentsMap}
            jobPositionsMap={jobPositionsMap}
            showSensitiveColumns={
              form.anonymous_mode === "identified" &&
              previewItems.some(
                (item: any) => item.respondent_token || item.delivery_status,
              )
            }
            headerActions={
              <Button
                variant="outline"
                onClick={handlePreview}
                disabled={previewMut.isPending || saving}
              >
                {previewMut.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
                Preview do público
              </Button>
            }
          />
        </div>
      )}
    </div>
  );
}
